import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import ApiService from '../../services/ApiService';
import {
  Container,
  PriceInputContainer,
  InputDiv,
  Label,
  Input,
  Select,
  PriceListContainer,
  PriceItemContainer,
  AddButton,
  DeleteButton
} from './style';

export interface PriceItem {
  price: number;
  unitOfMeasurementId: number;
}

export interface UnitOfMeasurement {
  id: number;
  name: string;
}

export interface PriceManagerRef {
  addPendingPrice: () => boolean;
}

interface PriceManagerProps {
  prices: PriceItem[];
  onChange: (prices: PriceItem[]) => void;
  baseUrl: string;
  label?: string;
  allowedUnits?: string[];
  /** Unidades já persistidas: linhas em leitura (o embed da venda é append-only). */
  lockedUnitIds?: number[];
}

const PriceManager = forwardRef<PriceManagerRef, PriceManagerProps>(({
  prices,
  onChange,
  baseUrl,
  label = "Preços",
  allowedUnits,
  lockedUnitIds
}, ref) => {
  const lockedIds = lockedUnitIds || [];
  const [units, setUnits] = useState<UnitOfMeasurement[]>([]);
  const [newPrice, setNewPrice] = useState<{ price: string; unitOfMeasurementId: string }>({
    price: '',
    unitOfMeasurementId: ''
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');

  const startEditing = (index: number, currentPrice: number) => {
    setEditingIndex(index);
    setEditingValue(formatCurrency(String(currentPrice * 100)));
  };

  const saveEdit = (index: number) => {
    const updatedPriceValue = parseCurrency(editingValue);
    const updatedPrices = prices.map((price, i) => i === index ? { ...price, price: updatedPriceValue } : price);
    onChange(updatedPrices);
    setEditingIndex(null);
    setEditingValue('');
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingValue('');
  };

  const formatCurrency = (value: string) => {
    const numericValue = value.replace(/\D/g, '');

    if (!numericValue) return '';

    const numberValue = parseInt(numericValue) / 100;

    return numberValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const parseCurrency = (value: string) => {
    return parseFloat(value.replace(/\./g, '').replace(',', '.')) || 0;
  };

  const handlePriceChange = (value: string) => {
    const formattedValue = formatCurrency(value);
    setNewPrice({ ...newPrice, price: formattedValue });
  };

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const isServices = baseUrl === `${process.env.REACT_APP_GATEWAY_BASE_URL}/services`;
        const response = isServices
          ? await ApiService.services.getUnits()
          : await ApiService.resources.getUnits();
        let unitsData = response.data || [];
        if (allowedUnits && allowedUnits.length > 0) {
          const allowedLower = allowedUnits.map(name => name.toLowerCase());
          unitsData = unitsData.filter((unit: any) => allowedLower.includes(unit.name?.toLowerCase()));
        } else if (baseUrl === `${process.env.REACT_APP_GATEWAY_BASE_URL}/services`) {
          const allowedNames = ['dia', 'hora', 'un'];
          unitsData = unitsData.filter((unit: any) => allowedNames.includes(unit.name?.toLowerCase()));
        }
        setUnits(unitsData);
      } catch (error) {
        console.error('Erro ao buscar unidades de medida:', error);
      }
    };

    fetchUnits();
  }, [baseUrl, allowedUnits]);

  const addPrice = () => {
    if (newPrice.price && newPrice.unitOfMeasurementId) {
      const unitExists = prices.some(price =>
        price.unitOfMeasurementId === parseInt(newPrice.unitOfMeasurementId)
      );

      if (unitExists) {
        alert('Esta unidade de medida já foi adicionada. Escolha uma unidade diferente.');
        return false;
      }

      onChange([...prices, {
        price: parseCurrency(newPrice.price),
        unitOfMeasurementId: parseInt(newPrice.unitOfMeasurementId)
      }]);
      setNewPrice({ price: '', unitOfMeasurementId: '' });
      return true;
    }
    return false;
  };

  const addPendingPrice = () => {
    if (newPrice.price && newPrice.unitOfMeasurementId) {
      const unitExists = prices.some(price =>
        price.unitOfMeasurementId === parseInt(newPrice.unitOfMeasurementId)
      );

      if (unitExists) {
        return false;
      }

      const newPriceItem = {
        price: parseCurrency(newPrice.price),
        unitOfMeasurementId: parseInt(newPrice.unitOfMeasurementId)
      };

      onChange([...prices, newPriceItem]);
      setNewPrice({ price: '', unitOfMeasurementId: '' });

      return true;
    }
    return false;
  };

  useImperativeHandle(ref, () => ({
    addPendingPrice
  }));

  const removePrice = (index: number) => {
    const updatedPrices = prices.filter((_, i) => i !== index);
    onChange(updatedPrices);
  };

  const availableUnits = units.filter(unit =>
    !prices.some(price => price.unitOfMeasurementId === unit.id)
  );

  const singular = label.toLowerCase().replace(/s$/, '');
  const singularCapitalized = singular.charAt(0).toUpperCase() + singular.slice(1);

  const getUnitName = (unitId: number) => {
    return units.find(unit => unit.id === unitId)?.name || '';
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  return (
    <Container>
      <PriceInputContainer>
        <InputDiv>
          <Label>{singularCapitalized} (R$)</Label>
          <Input
            type="text"
            value={newPrice.price}
            onChange={(e) => handlePriceChange(e.target.value)}
            placeholder="0,00"
          />
        </InputDiv>

        <InputDiv>
          <Label>Unidade</Label>
          <Select
            value={newPrice.unitOfMeasurementId}
            onChange={(e) => setNewPrice({ ...newPrice, unitOfMeasurementId: e.target.value })}
          >
            <option value=""></option>
            {availableUnits.map(unit => (
              <option key={unit.id} value={unit.id}>
                {unit.name}
              </option>
            ))}
          </Select>
        </InputDiv>
      </PriceInputContainer>

      <AddButton
        onClick={addPrice}
        disabled={!newPrice.price || !newPrice.unitOfMeasurementId}
      >
        <Plus size={16} />
        {prices.length === 0 ? `Adicionar ${label.toLowerCase()}` : `Adicionar outro ${singular}`}
      </AddButton>

      {prices.length > 0 && (
        <PriceListContainer>
          {prices.map((price, index) => (
            <PriceItemContainer key={index}>
              {editingIndex === index ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                  <Input
                    type="text"
                    value={editingValue}
                    onChange={(e) => setEditingValue(formatCurrency(e.target.value))}
                    style={{ flex: 1, padding: '0 8px', height: '30px', borderRadius: '4px' }}
                  />
                  <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>
                    / {getUnitName(price.unitOfMeasurementId)}
                  </span>
                  <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                    <button 
                      onClick={() => saveEdit(index)} 
                      disabled={!editingValue}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10b981', display: 'flex', alignItems: 'center' }}
                    >
                      <Check size={16} />
                    </button>
                    <button 
                      onClick={cancelEdit}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <span>
                    {formatPrice(price.price)} / {getUnitName(price.unitOfMeasurementId)}
                  </span>
                  {!lockedIds.includes(price.unitOfMeasurementId) && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button 
                        onClick={() => startEditing(index, price.price)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#007bff', display: 'flex', alignItems: 'center', padding: 0 }}
                      >
                        <Pencil size={12} />
                      </button>
                      <DeleteButton onClick={() => removePrice(index)} style={{ margin: 0 }}>
                        <Trash2 size={12} />
                      </DeleteButton>
                    </div>
                  )}
                </>
              )}
            </PriceItemContainer>
          ))}
        </PriceListContainer>
      )}
    </Container>
  );
});

PriceManager.displayName = 'PriceManager';

export default PriceManager;

